<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'" :class="embedded ? '' : 'app-card'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded" class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Add Product
        </v-card-title>
        <v-card-text class="pf-body">
          <!-- Basic Information -->
          <v-card outlined class="pf-section">
            <div class="pf-section__header" @click="toggleSection('basic')">
              <span class="pf-section__icon"><v-icon color="primary">mdi-file-document-outline</v-icon></span>
              <span class="pf-section__heading">
                <span class="pf-section__title">Basic Information</span>
                <span class="pf-section__subtitle">Add the essential details about your product</span>
              </span>
              <v-spacer></v-spacer>
              <v-icon>{{ sections.basic ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="sections.basic" class="pf-section__body">
                <v-row dense>
                  <v-col sm="4">
                    <v-text-field
                      v-model="form.name"
                      outlined
                      dense
                      prepend-inner-icon="mdi-package-variant"
                      :error-messages="errors.name"
                    >
                      <template v-slot:label>Product Name <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-text-field>
                  </v-col>
                  <v-col sm="4">
                    <v-text-field
                      v-model="form.hsn_code"
                      outlined
                      dense
                      label="HSN Code (optional)"
                      prepend-inner-icon="mdi-barcode"
                      :error-messages="errors.hsn_code"
                    ></v-text-field>
                  </v-col>
                  <v-col sm="4">
                    <v-autocomplete
                      v-model="mainCategorySelected"
                      :items="(categoryLevels[0] && categoryLevels[0].options) || []"
                      outlined
                      dense
                      prepend-inner-icon="mdi-shape"
                      item-text="name"
                      item-value="id"
                      clearable
                      hide-details="auto"
                      @change="onCategoryLevelChange(0)"
                      :error-messages="errors.category_id"
                    >
                      <template v-slot:label>Category <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <!-- Subcategory levels, if this category goes deeper than one level -->
                <v-row dense v-if="categoryLevels.length > 1" class="mt-1">
                  <v-col
                    v-for="(level, index) in categoryLevels.slice(1)"
                    :key="`level_${index + 1}`"
                    sm="4"
                  >
                    <v-autocomplete
                      v-model="level.selected"
                      :items="level.options"
                      outlined
                      dense
                      prepend-inner-icon="mdi-chevron-right"
                      item-text="name"
                      item-value="id"
                      clearable
                      hide-details="auto"
                      :label="level.label"
                      no-data-text="No sub-categories yet - add one from Catalog > Sub-Category"
                      @change="onCategoryLevelChange(index + 1)"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
                <v-breadcrumbs
                  v-if="categoryPath.length"
                  :items="categoryPath"
                  divider=">"
                  class="pa-0 mt-1"
                >
                  <template v-slot:item="{ item }">
                    <v-breadcrumbs-item>
                      <v-chip x-small color="primary" text-color="white">
                        {{ item.text }}
                      </v-chip>
                    </v-breadcrumbs-item>
                  </template>
                </v-breadcrumbs>

                <v-row dense class="mt-1">
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.description"
                      outlined
                      dense
                      rows="2"
                      auto-grow
                      label="Description (optional)"
                      prepend-inner-icon="mdi-text"
                      :error-messages="errors.description"
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <div class="pf-field-label">Product Details <span class="grey--text font-weight-regular">(optional)</span></div>
                    <vue-editor
                      ref="refund policy"
                      v-model="form.product_details"
                      class="form-control pf-compact-editor"
                    />
                    <p v-if="errors.product_details" class="red--text caption mt-1">
                      {{ errors.product_details[0] }}
                    </p>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-card>

          <v-card outlined class="pf-section">
            <div class="pf-section__header" @click="toggleSection('pricing')">
              <span class="pf-section__icon"><v-icon color="primary">mdi-truck-delivery-outline</v-icon></span>
              <span class="pf-section__heading">
                <span class="pf-section__title">Pricing & Delivery</span>
                <span class="pf-section__subtitle">Set delivery charges and applicable tax rate</span>
              </span>
              <v-spacer></v-spacer>
              <v-icon>{{ sections.pricing ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="sections.pricing" class="pf-section__body">
                <v-row dense align="start">
                  <v-col :sm="form.delivery_type == 1 ? 4 : 6">
                    <v-autocomplete
                      ref="position"
                      outlined
                      dense
                      v-model="form.delivery_type"
                      :items="deliveryItems"
                      prepend-inner-icon="mdi-truck-delivery"
                      clearable
                      hide-details="auto"
                      :error-messages="errors.delivery_type"
                    >
                      <template v-slot:label>Delivery Type <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-autocomplete>
                  </v-col>
                  <v-col sm="4" v-if="form.delivery_type == 1">
                    <v-text-field
                      v-model="form.delivery_amount"
                      outlined
                      dense
                      prepend-inner-icon="mdi-currency-usd"
                      hide-details="auto"
                      :error-messages="errors.delivery_amount"
                    >
                      <template v-slot:label>Delivery Amount <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-text-field>
                  </v-col>
                  <v-col :sm="form.delivery_type == 1 ? 4 : 6">
                    <v-autocomplete
                      v-model="form.gst_value_list_id"
                      :items="gstRates"
                      item-text="text"
                      item-value="value"
                      label="GST Rate"
                      outlined
                      dense
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-percent"
                      :error-messages="errors.gst_value_list_id"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-card>

          <!-- Variants -->
          <v-card outlined class="pf-section" v-if="form.product_variants">
            <div class="pf-section__header" @click="toggleSection('variants')">
              <span class="pf-section__icon"><v-icon color="primary">mdi-tune-variant</v-icon></span>
              <span class="pf-section__heading">
                <span class="pf-section__title">Variants</span>
                <span class="pf-section__subtitle">Add product variants (size, color, etc.)</span>
              </span>
              <v-spacer></v-spacer>
              <v-btn small rounded color="primary" @click.stop="addVariant" class="mr-2">
                <v-icon left small>mdi-plus</v-icon> Add Variant
              </v-btn>
              <v-icon>{{ sections.variants ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="sections.variants" class="pf-section__body">
                <p class="text-caption grey--text mt-n1 mb-3">
                  <template v-if="embedded">Enter a Qty and Cost Price to have this variant added straight to the Purchase Order you're creating — leave Qty at 0 to just add it to the catalog.</template>
                  <template v-else>New products always start with 0 stock — add stock for a variant via a Purchase Order.</template>
                </p>

                <v-card
                  outlined
                  class="pf-variant mb-4"
                  v-for="(variant, variant_index) in form.product_variants"
                  :key="`variant${variant_index}`"
                >
                  <div class="pf-variant__header">
                    <span class="pf-variant__title">Variant {{ variant_index + 1 }}</span>
                    <v-chip v-if="variant_index === 0" x-small color="primary" text-color="white" class="ml-2 font-weight-bold">Default</v-chip>
                    <v-spacer></v-spacer>
                    <v-dialog
                      v-model="isDeleteVariantDialogs[variant_index]"
                      max-width="600px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <a href="#" class="pf-variant__remove" v-bind="attrs" v-on="on" @click.prevent>
                          <v-icon small color="error">mdi-delete-outline</v-icon> Remove
                        </a>
                      </template>
                      <v-card elevation="10">
                        <v-card-title>
                          <v-icon large color="primary">
                            mdi-information-outline
                          </v-icon>
                          &nbsp; Do you want to remove the variant?
                        </v-card-title>
                        <v-card-text>
                          <v-btn
                            color="primary"
                            @click.stop="deleteVariant(variant_index)"
                          >
                            Yes
                          </v-btn>
                          &nbsp;
                          <v-btn
                            color="red"
                            dark
                            @click.stop="isDeleteVariantDialogs = []"
                          >
                            No
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </v-dialog>
                  </div>
                  <v-divider></v-divider>
                  <v-card-text>
                    <div class="pf-field-label">Images <span class="grey--text text-caption">(optional)</span></div>
                    <div class="pf-image-strip">
                      <div
                        class="pf-image-tile"
                        v-for="(image, image_index) in variant.images"
                        :key="`variant_${variant_index}_image_${image_index}`"
                      >
                        <template v-if="image.preview_url">
                          <v-img :src="image.preview_url" class="pf-image-tile__img" aspect-ratio="1"></v-img>
                          <button
                            type="button"
                            class="pf-image-tile__remove"
                            title="Remove image"
                            @click="removeVariantImage(variant_index, image_index)"
                          >
                            <v-icon x-small color="white">mdi-close</v-icon>
                          </button>
                        </template>
                        <label v-else class="pf-image-tile__upload">
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            @change="onImageTileChange($event, variant_index, image_index)"
                          />
                          <v-icon color="grey">mdi-camera-plus-outline</v-icon>
                          <span>{{ image_index === 0 ? "Primary" : "Upload" }}</span>
                        </label>
                      </div>
                      <button type="button" class="pf-image-tile pf-image-tile--add" title="Add another image" @click="addVariantImage(variant_index)">
                        <v-icon color="primary">mdi-plus</v-icon>
                      </button>
                    </div>
                    <p v-if="errors[`product_variants.${variant_index}.images`]" class="red--text caption mt-1 mb-0">
                      {{ errors[`product_variants.${variant_index}.images`][0] }}
                    </p>

                    <v-row class="mt-3">
                      <v-col sm="4">
                        <ColorAutocomplete
                          ref="color"
                          outlined
                          dense
                          v-model="variant.color"
                          :items="colorItems"
                          prepend-inner-icon="mdi-palette"
                          clearable
                          hide-details="auto"
                          :error-messages="
                            errors[`product_variants.${variant_index}.color`]
                          "
                          @created="(color) => colorItems.push(color)"
                        >
                          <template v-slot:label>Color (optional)</template>
                        </ColorAutocomplete>
                      </v-col>
                      <v-col sm="4">
                        <v-autocomplete
                          ref="size"
                          outlined
                          dense
                          v-model="variant.size"
                          :items="sizeItems"
                          label="Size"
                          prepend-inner-icon="mdi-ruler"
                          clearable
                          hide-details="auto"
                          :error-messages="
                            errors[`product_variants.${variant_index}.size`]
                          "
                        ></v-autocomplete>
                      </v-col>
                      <v-col sm="4">
                        <v-text-field
                          v-model.number="variant.quantity"
                          type="number"
                          min="0"
                          outlined
                          dense
                          label="Qty"
                          prepend-inner-icon="mdi-card-text-outline"
                          hide-details="auto"
                          :error-messages="
                            errors[`product_variants.${variant_index}.quantity`]
                          "
                        ></v-text-field>
                      </v-col>
                      <v-col sm="4" v-if="embedded">
                        <v-text-field
                          v-model="variant.cost_price"
                          outlined
                          dense
                          label="Cost Price"
                          prepend-inner-icon="mdi-currency-usd"
                          hide-details="auto"
                          @input="applyMarkup(variant)"
                        ></v-text-field>
                      </v-col>
                      <v-col sm="4" v-if="embedded">
                        <v-text-field
                          v-model="variant.margin_percent"
                          type="number"
                          min="0"
                          outlined
                          dense
                          label="Markup % on Cost"
                          prepend-inner-icon="mdi-percent"
                          hide-details="auto"
                          @input="applyMarkup(variant)"
                        ></v-text-field>
                      </v-col>
                      <v-col sm="4">
                        <v-text-field
                          v-model="variant.price"
                          outlined
                          dense
                          prepend-inner-icon="mdi-currency-usd"
                          hide-details="auto"
                          @input="variant.margin_percent = ''"
                          :error-messages="
                            errors[`product_variants.${variant_index}.price`]
                          "
                        >
                          <template v-slot:label>Selling Price <span style="color:#f44336;font-weight:bold;">*</span></template>
                        </v-text-field>
                      </v-col>
                      <v-col sm="4">
                        <v-text-field
                          v-model="variant.discount_percentage"
                          outlined
                          dense
                          label="Discount %"
                          prepend-inner-icon="mdi-percent"
                          hide-details="auto"
                          :error-messages="
                            errors[
                              `product_variants.${variant_index}.discount_percentage`
                            ]
                          "
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <p v-if="variantsError || errors.product_variants" class="red--text caption mt-1">
                  {{ variantsError || (errors.product_variants && errors.product_variants[0]) }}
                </p>
              </div>
            </v-expand-transition>
          </v-card>
        </v-card-text>

        <div class="pf-footer">
          <v-btn v-if="embedded" text @click="$emit('close')">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            <v-icon left small>mdi-content-save-outline</v-icon> Save Product
          </v-btn>
        </div>
      </v-card>
    </component>
  </component>
</template>

<script>
import { VueEditor } from "vue2-editor";
import axios from "axios";
import ColorAutocomplete from "@/components/ColorAutocomplete.vue";

export default {
  name: "ProductCreatePage",
  components: {
    VueEditor,
    ColorAutocomplete,
  },
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Purchase Order create screen's inline "Create Product") instead of
    // its own routed page - it emits `created`/`close` rather than
    // navigating away, and drops its own page-level layout wrapper.
    embedded: { type: Boolean, default: false },
    // Name typed into the picker before choosing "Create" - pre-fills Product Name.
    initialName: { type: String, default: "" },
  },
  data() {
    return {
      form: {
        role_id: 3,
        name: this.initialName ? this.initialName.trim() : "",
        hsn_code: "",
        description: "",
        product_details: "",
        ratings: "",
        delivery_type: 0,
        delivery_amount: "",
        category_id: null,
        gst_value_list_id: null,
        product_variants: [
          {
            color: "",
            size: "",
            price: "",
            cost_price: "",
            margin_percent: "",
            discount_percentage: "",
            quantity: 0,
            images: [this.createEmptyImage()],
          },
        ],
      },
      isLoading: false,
      colorItems: [],
      sizeItems: [],
      gstRates: [],
      isDeleteVariantDialogs: [],
      variantsError: '',
      deliveryItems: [
        { text: "FREE DELIVERY", value: 0 },
        { text: "PAID DELIVERY", value: 1 },
      ],
      // Multi-level category selection
      categoryLevels: [],
      categoryPath: [],
      // Each section card can be collapsed independently; all start open.
      sections: {
        basic: true,
        pricing: true,
        variants: true,
      },
    };
  },

  computed: {
    // categoryLevels[0] doesn't exist until fetchCategories() resolves, so
    // v-model can't bind straight to it (categoryLevels[0].selected would
    // throw on the first render) - this fills in safely until then.
    mainCategorySelected: {
      get() {
        return this.categoryLevels[0] ? this.categoryLevels[0].selected : null;
      },
      set(value) {
        if (this.categoryLevels[0]) {
          this.categoryLevels[0].selected = value;
        }
      },
    },
  },

  mounted() {
    this.$store.dispatch('errors/setErrors', {});
    this.fetchCategories();
    this.getMasters();
    this.fetchGSTRates();
  },

  methods: {
    toggleSection(key) {
      this.sections[key] = !this.sections[key];
    },
    createEmptyImage() {
      return {
        id: null,
        original_path: null,
        preview_url: null,
        image_path: null,
        fileInput: null,
        has_new_upload: false,
        is_deleted: false,
      };
    },

    async getMasters() {
      try {
        const masters = await axios.get(`masters?context=colorAndsize`);
        const data = masters.data;

        // Handle colors
        data.colors.forEach((color) => {
          this.colorItems.push({
            id: color.id,
            text: color.description,
            value: color.code,
          });
        });

        // Handle sizes
        data.sizes.forEach((size) => {
          this.sizeItems.push({
            id: size.id,
            text: size.description,
            value: size.code,
          });
        });
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async fetchGSTRates() {
      try {
        const response = await axios.get("gst-rates");
        if (response.data.success) {
          this.gstRates = response.data.data.map((rate) => ({
            text: rate.description + "%",
            value: rate.id,
          }));
        }
      } catch (error) {
        console.error("Error fetching GST rates:", error);
        if (error.response?.status !== 404) {
          this.$toast.error("Failed to load GST rates");
        }
      }
    },

    async fetchCategories() {
      try {
        const response = await axios.get("masters?context=categories");
        const rootCategories = response.data.categories.filter(
          (category) => category.parent_id === null
        );

        // Initialize first level
        this.categoryLevels = [
          {
            level: 1,
            label: "Main Category (Level 1)",
            selected: null,
            options: rootCategories,
          },
        ];
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    async onCategoryLevelChange(levelIndex) {
      const currentLevel = this.categoryLevels[levelIndex];

      // Remove all levels after the current one
      this.categoryLevels = this.categoryLevels.slice(0, levelIndex + 1);

      // Update category path
      this.updateCategoryPath();

      // If nothing selected, clear form category_id
      if (!currentLevel.selected) {
        this.form.category_id = null;
        return;
      }

      // Set the selected category as the current category_id
      this.form.category_id = currentLevel.selected;

      // Try to load subcategories for the selected category
      try {
        const response = await axios.get(
          `/masters?context=categories&parent_id=${currentLevel.selected}`
        );
        const subcategories = response.data.categories;

        // A top-level category always gets the (optional) Sub-Category box,
        // even before any exist; deeper levels only appear when they exist.
        if ((subcategories && subcategories.length > 0) || levelIndex === 0) {
          this.categoryLevels.push({
            level: levelIndex + 2,
            label: this.subCategoryLabel(levelIndex + 2),
            selected: null,
            options: subcategories || [],
          });
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    },

    // Level 2 is the Sub-Category; anything deeper keeps its level number
    subCategoryLabel(level) {
      return level === 2 ? "Sub-Category (optional)" : `Sub-Category (Level ${level})`;
    },

    updateCategoryPath() {
      this.categoryPath = [];
      this.categoryLevels.forEach((level) => {
        if (level.selected) {
          const selectedCategory = level.options.find(
            (cat) => cat.id === level.selected
          );
          if (selectedCategory) {
            this.categoryPath.push({
              text: selectedCategory.name,
            });
          }
        }
      });
    },

    addVariant() {
      const newVariant = {
        color: "",
        size: "",
        price: "",
        cost_price: "",
        margin_percent: "",
        discount_percentage: "",
        quantity: 0,
        images: [this.createEmptyImage()],
      };
      this.form.product_variants.push(newVariant);
    },

    // Selling Price = Cost Price + Markup % of Cost Price (e.g. 200 at 50% -> 300).
    // Only fills when both are filled in, so typing a price by hand is never
    // overwritten; typing in Selling Price clears the markup instead.
    applyMarkup(variant) {
      const cost = parseFloat(variant.cost_price);
      const pct = parseFloat(variant.margin_percent);
      if (!(cost > 0) || isNaN(pct)) return;
      variant.price = (Math.round(cost * (1 + pct / 100) * 100) / 100).toString();
    },

    deleteVariant(variantIndex) {
      if (this.form.product_variants.length <= 1) {
        this.$toast.error('At least one product variant is required');
        this.isDeleteVariantDialogs = [];
        return;
      }
      // Clean up any blob URLs before removing variant
      const variant = this.form.product_variants[variantIndex];
      if (variant.images) {
        variant.images.forEach((img) => {
          if (img.preview_url && img.preview_url.startsWith("blob:")) {
            URL.revokeObjectURL(img.preview_url);
          }
        });
      }

      this.form.product_variants.splice(variantIndex, 1);
      this.isDeleteVariantDialogs = [];
    },

    addVariantImage(variantIndex) {
      const variant = this.form.product_variants[variantIndex];
      if (!variant.images) {
        this.$set(variant, "images", []);
      }
      variant.images.push(this.createEmptyImage());
    },

    handleImageUpload(file, variantIndex, imageIndex) {
      const image = this.form.product_variants[variantIndex].images[imageIndex];

      if (file) {
        // Clean up previous blob URL if exists
        if (image.preview_url && image.preview_url.startsWith("blob:")) {
          URL.revokeObjectURL(image.preview_url);
        }

        // Set new file and create preview
        image.image_path = file;
        image.fileInput = file;
        image.preview_url = URL.createObjectURL(file);
        image.has_new_upload = true;
        image.is_deleted = false;

        console.log(
          `Image uploaded for variant ${variantIndex}, image ${imageIndex}:`,
          {
            fileName: file.name,
            fileSize: file.size,
          }
        );
      } else {
        this.clearImageUpload(variantIndex, imageIndex);
      }
    },

    clearImageUpload(variantIndex, imageIndex) {
      const image = this.form.product_variants[variantIndex].images[imageIndex];

      // Clean up blob URL if it exists
      if (image.preview_url && image.preview_url.startsWith("blob:")) {
        URL.revokeObjectURL(image.preview_url);
      }

      // Reset to empty state
      image.image_path = null;
      image.fileInput = null;
      image.preview_url = null;
      image.has_new_upload = false;
    },

    removeVariantImage(variantIndex, imageIndex) {
      const images = this.form.product_variants[variantIndex].images;
      const image = images[imageIndex];

      // Clean up blob URL
      if (image.preview_url && image.preview_url.startsWith("blob:")) {
        URL.revokeObjectURL(image.preview_url);
      }

      // Since this is create page, we can safely remove the image completely
      images.splice(imageIndex, 1);

      // Always keep at least one (empty) tile so the primary upload slot
      // never disappears (images themselves are optional).
      if (images.length === 0) {
        images.push(this.createEmptyImage());
      }
    },

    // Adapts a native <input type="file"> change event (used by the compact
    // image-strip tiles) to handleImageUpload()'s expected (file, ...) shape.
    onImageTileChange(event, variantIndex, imageIndex) {
      const file = event.target.files && event.target.files[0];
      this.handleImageUpload(file || null, variantIndex, imageIndex);
      event.target.value = "";
    },

    prepareFormDataForSubmission() {
      const formData = new FormData();

      // Add basic product fields
      Object.keys(this.form).forEach((key) => {
        if (key === "product_variants") return;
        if (key === "category_id") {
          formData.append(key, this.form[key] ?? '');
          return;
        }
        if (this.form[key] !== null && this.form[key] !== undefined) {
          formData.append(key, this.form[key]);
        }
      });

      // Process variants
      this.form.product_variants.forEach((variant, variantIndex) => {
        // Add variant fields. `cost_price` is never sent - there's no such
        // column on the product; when embedded it's only used locally to
        // seed the Purchase Order's line items after this call succeeds.
        // Embedded mode also always submits quantity as 0: any qty entered
        // here is meant to arrive as stock through that same Purchase
        // Order (with its cost tracked), not created directly bypassing it.
        Object.keys(variant).forEach((key) => {
          if (key === "images" || key === "cost_price" || key === "margin_percent") return;
          const value = key === "quantity" && this.embedded ? 0 : variant[key];
          if (value !== null && value !== undefined) {
            formData.append(
              `product_variants[${variantIndex}][${key}]`,
              value
            );
          }
        });

        // Process images for this variant
        if (variant.images) {
          let imageIndex = 0;
          variant.images.forEach((image) => {
            // Skip deleted or empty images
            if (image.is_deleted || !image.image_path) return;

            // Add new file upload
            if (image.image_path instanceof File) {
              formData.append(
                `product_variants[${variantIndex}][images][${imageIndex}][image_path]`,
                image.image_path
              );
            }

            imageIndex++;
          });
        }
      });

      return formData;
    },

    async save() {
      try {
        this.isLoading = true;
        this.variantsError = '';
        this.$store.dispatch('errors/setErrors', {});

        // Snapshot what was typed for Qty/Cost Price before submission -
        // sent quantities are zeroed out for embedded mode (see
        // prepareFormDataForSubmission), so this is the only record of what
        // the seller actually asked to stock in.
        const enteredQuantities = this.form.product_variants.map((v) => Number(v.quantity) || 0);
        const enteredCostPrices = this.form.product_variants.map((v) => Number(v.cost_price) || 0);

        const formData = this.prepareFormDataForSubmission();

        const response = await axios.post("/products", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (this.embedded) {
          const createdVariants = response.data.data.created_variants || [];
          const seedItems = createdVariants
            .map((cv, index) => ({
              variant_id: cv.id,
              display_name: cv.display_name,
              quantity: enteredQuantities[index] || 0,
              cost_price: enteredCostPrices[index] || 0,
              // Null on purpose: the variant was just created with this price
              // (and any Discount %); re-sending it from the PO would only
              // make the backend wipe that discount.
              selling_price: null,
            }))
            .filter((seed) => seed.quantity > 0);

          this.$emit("created", { ...response.data.data, seed_items: seedItems });
        } else {
          this.$router.push('/products');
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.$store.dispatch('errors/setErrors', error.response.data.errors);
          if (this.$toast) this.$toast.error("Please fix the validation errors");
        } else {
          if (this.$toast) this.$toast.error("Failed to create product");
        }
      } finally {
        this.isLoading = false;
      }
    },
  },

  beforeDestroy() {
    this.$store.dispatch('errors/setErrors', {});
    // Clean up all blob URLs to prevent memory leaks
    if (this.form.product_variants) {
      this.form.product_variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) => {
            if (img.preview_url && img.preview_url.startsWith("blob:")) {
              URL.revokeObjectURL(img.preview_url);
            }
          });
        }
      });
    }
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}

.pf-body {
  padding-bottom: 4px;
}

/* Section cards (Basic Information / Pricing & Delivery / Variants) */
.pf-section {
  margin-bottom: 14px;
  overflow: hidden;
}
.pf-section__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--color-surface-alt, #f5f3ef);
  cursor: pointer;
  user-select: none;
}
.pf-section__icon {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(202, 181, 150, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pf-section__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.pf-section__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--color-ink);
}
.pf-section__subtitle {
  font-size: 0.74rem;
  color: var(--color-ink);
  opacity: 0.6;
}
.pf-section__body {
  padding: 12px 16px;
}
.pf-section__body .row {
  margin-top: 0;
  margin-bottom: 0;
}
.pf-section__body .row + .row {
  margin-top: 4px;
}
.pf-field-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-ink);
  opacity: 0.85;
  margin-bottom: 4px;
}

/* Product Details editor kept compact - about 3 lines, scrolls past that */
.pf-compact-editor >>> .ql-editor {
  min-height: 66px;
  max-height: 66px;
  overflow-y: auto;
}
.pf-compact-editor >>> .ql-container {
  font-size: 0.85rem;
}

/* Variant cards inside the Variants section */
.pf-variant {
  overflow: hidden;
}
.pf-variant__header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--color-surface-alt, #f5f3ef);
}
.pf-variant__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--color-ink);
}
.pf-variant__remove {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-error);
  text-decoration: none;
}
.pf-variant__preview {
  border: 1px solid #e0dccf;
}

/* Sticky footer with Cancel / Save Product */
.pf-footer {
  flex: none;
  display: flex;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid #ece7de;
  background: var(--color-surface);
}

/* Variant image thumbnail strip */
.pf-image-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.pf-image-tile {
  position: relative;
  width: 84px;
  height: 84px;
  flex: none;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0dccf;
  background: var(--color-surface-alt, #f5f3ef);
}
.pf-image-tile__img {
  width: 100%;
  height: 100%;
}
.pf-image-tile__upload {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  color: #8a8272;
  font-size: 0.65rem;
  text-align: center;
}
.pf-image-tile__remove {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.pf-image-tile--add {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--color-primary);
  background: transparent;
  cursor: pointer;
}
</style>
